import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  Sparkles,
  Upload,
  X,
  Trash2,
} from 'lucide-react';
import { uploadImage } from '@/lib/images/upload';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import type { CompressPreset } from '@/lib/images/compress';
import { brandsApi, brandkitApi } from '@/features/brands/api';
import { queryKeys } from '@/lib/api/queryKeys';
import type { BrandKit, CoCompany, XkBrand } from '@/lib/api/types';
import { useCurrentCompany } from '@/lib/branding/useCurrentBrand';
import { Skeleton } from '@/components/ui/Skeleton';
import { toast } from '@/components/ui/Toast';
import { THEME, BRAND_DEFAULTS } from '@/lib/theme';
import { useT } from '@/lib/i18n/useT';
// PRD §13.5 — TCPA acknowledgement is required before enabling Variant B
// (PII + unbundled marketing opt-in) on the consumer flow.

// ════════════════════════════════════════════════════════════════════════════
// Config — mirrors `Xotski-v2/Brand Studio · Default` BrandStudioConfig
// ════════════════════════════════════════════════════════════════════════════

const FONTS = [
  'Inter',
  'DM Sans',
  'Space Grotesk',
  'Outfit',
  'Plus Jakarta Sans',
  'Montserrat',
  'Bebas Neue',
  'Sora',
  'Roboto',
  'Poppins',
  'Lato',
  'Open Sans',
] as const;

type Celebration = 'confetti' | 'fireworks' | 'sparks' | 'soccer' | 'none';
type ButtonStyle = 'rounded' | 'square' | 'pill';
type AgeGateOpt = 'none' | '18+' | '21+';

// Studio Prizes tab now reads xk_stock rows scoped to the brand and
// allows adopting prizes from the parent agency's catalogue via
// `stockApi.agencyCatalog` + `stockApi.adopt`. See PrizesTab below.

interface BrandStudioConfig {
  // Identity
  name: string;
  industry: string;
  campaignName: string;
  brandLogo: string | null;
  qrThumbnail: string | null;
  backgroundImage: string | null;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  backgroundColor: string;
  textColor: string;
  font: string;
  // Experience
  language: 'en' | 'es' | 'en-es';
  collectData: boolean;
  collectName: boolean;
  collectEmail: boolean;
  collectPhone: boolean;
  ageGate: AgeGateOpt;
  demoAge: boolean;
  demoGender: boolean;
  // PRD §8 Tab 2 — inclusive protocol pronouns toggle (free tier).
  demoPronouns: boolean;
  demoEthnicity: boolean;
  demoNationality: boolean;
  // PRD §8 Tab 2 — number of custom questions added (gated by plan tier
  // 0/2/5/5/inf for Pilot/Scale/Program/Enterprise/...). The list itself
  // ships in Phase 4; this field carries the count so the quota chip can
  // render today.
  customQuestionsCount: number;
  // Look & Feel
  buttonStyle: ButtonStyle;
  celebrationTheme: Celebration;
  consentWording: string;
  // PRD §11 Screen 3 — consent flow variant.
  //   'A' = anonymous (no PII, no marketing checkbox)
  //   'B' = PII collection + unbundled marketing opt-in (default)
  consentVariant: 'A' | 'B';
  // PRD §16 — Atledi voice preset that shapes the recap + welcome
  // narrative. Stored at the brand level so every event the brand runs
  // inherits the same voice unless overridden per-event.
  atlediTone: 'playful' | 'professional' | 'warm' | 'bold' | 'custom';
  atlediToneCustom: string;
  gameCTAEn: string;
  gameCTAEs: string;
  // PRD §11 Screen 6 — post-claim redirect mode.
  //   'none'     = Onxite finish screen (default)
  //   'branded'  = stay in Xotski, show branded thank-you panel
  //   'external' = redirect to postClaimRedirectUrl
  postClaimRedirect: 'none' | 'branded' | 'external';
  postClaimRedirectUrl: string;
  // Game
  gameMechanic: string;
  gameTier: 'free' | 'premium' | 'pro';
  wheelTaglineEn: string;
  wheelTaglineEs: string;
  wheelHeroImage: string | null;
  // PRD §9 v2 — Prizes no longer live on this local config blob; they are
  // persisted as `xk_stock` rows owned by the brand and fetched by
  // `PrizesTab` from the backend on render.
}

/**
 * CH-02 — Al reabrir una marca, lo que se guardó en `co_brandkit` (paleta de
 * cinco colores, nombre de campaña, fuente, ajustes de experiencia) tiene que
 * volver. Antes sólo se leía la fila de la empresa, que guarda tres colores:
 * acento y fondo salían derivados y el nombre de campaña, vacío.
 */
function configFromBrand(b: XkBrand, kit: BrandKit | null): BrandStudioConfig {
  const base = configFromCompany(b);
  if (!kit) return { ...base, name: b.name ?? '' };
  const pick = <T,>(v: T | null | undefined, fallback: T): T => (v == null || v === '' ? fallback : v);
  return {
    ...base,
    name: b.name ?? '',
    campaignName: pick(kit.campaignName, base.campaignName),
    brandLogo: pick(kit.brandLogo, base.brandLogo),
    backgroundImage: pick(kit.backgroundImage, base.backgroundImage),
    primaryColor: pick(kit.primaryColor, base.primaryColor),
    secondaryColor: pick(kit.secondaryColor, base.secondaryColor),
    accentColor: pick(kit.accentColor, base.accentColor),
    backgroundColor: pick(kit.backgroundColor, base.backgroundColor),
    textColor: pick(kit.fontColor, base.textColor),
    font: pick(kit.font, base.font),
    language: pick(kit.language as BrandStudioConfig['language'] | null, base.language),
    collectData: kit.collectData ?? base.collectData,
    collectName: kit.collectName ?? base.collectName,
    collectEmail: kit.collectEmail ?? base.collectEmail,
    collectPhone: kit.collectPhone ?? base.collectPhone,
    ageGate: pick(kit.ageGate as BrandStudioConfig['ageGate'] | null, base.ageGate),
    demoAge: kit.demoAge ?? base.demoAge,
    demoGender: kit.demoGender ?? base.demoGender,
    demoEthnicity: kit.demoEthnicity ?? base.demoEthnicity,
    demoNationality: kit.demoNationality ?? base.demoNationality,
    celebrationTheme: pick(
      (kit as { celebrationTheme?: string | null }).celebrationTheme as BrandStudioConfig['celebrationTheme'] | null,
      base.celebrationTheme,
    ),
  };
}

function configFromCompany(c: CoCompany | null): BrandStudioConfig {
  return {
    name: '',
    industry: c?.industry ?? '',
    campaignName: '',
    brandLogo: c?.logo ?? null,
    qrThumbnail: null,
    backgroundImage: c?.backgroundImage ?? null,
    primaryColor: c?.backgroundColor ?? BRAND_DEFAULTS.primaryColor,
    secondaryColor: c?.secondaryColor ?? THEME.navy2,
    accentColor: c?.secondaryColor ?? BRAND_DEFAULTS.accentColor,
    backgroundColor: c?.backgroundColor ?? BRAND_DEFAULTS.primaryColor,
    textColor: c?.fontColor ?? '#FFFFFF',
    font: BRAND_DEFAULTS.font,
    language: 'en',
    collectData: true,
    collectName: true,
    collectEmail: true,
    collectPhone: false,
    ageGate: 'none',
    demoAge: true,
    demoGender: true,
    demoPronouns: false,
    demoEthnicity: false,
    demoNationality: false,
    customQuestionsCount: 0,
    buttonStyle: 'rounded',
    celebrationTheme: 'confetti',
    consentWording:
      'By continuing, you agree to our Terms of Service and Privacy Policy. Your data may be shared with the event sponsor.',
    consentVariant: 'B',
    atlediTone: 'warm',
    atlediToneCustom: '',
    postClaimRedirect: 'none',
    postClaimRedirectUrl: '',
    gameCTAEn: 'Spin to win!',
    gameCTAEs: '¡Gira para ganar!',
    gameMechanic: 'wheel',
    gameTier: 'free',
    wheelTaglineEn: '',
    wheelTaglineEs: '',
    wheelHeroImage: null,
  };
}

// ════════════════════════════════════════════════════════════════════════════
// Page
// ════════════════════════════════════════════════════════════════════════════

// Brand Studio es SÓLO la identidad de la marca (logotipo, paleta,
// tipografía). Lo que antes tenían las pestañas Experience, Look & Feel,
// Game y Output lo decide hoy el Design Journey por despliegue, y los premios
// viven en la ficha de la marca (/brand/:id). Retiradas el 26 sep 2026.

export function BrandStudioNewPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const t = useT();
  const { id } = useParams<{ id?: string }>();
  const brandId = id ? Number(id) : null;
  const isEdit = Boolean(brandId);
  const { company, isLoading: companyLoading } = useCurrentCompany();
  const kitQ = useQuery({
    queryKey: queryKeys.brandKit.byCompany(brandId ?? 0),
    queryFn: () => brandkitApi.get(brandId!),
    enabled: isEdit && !!brandId,
    retry: false,
  });
  const brandQ = useQuery({
    queryKey: queryKeys.brands.detail(brandId ?? 0),
    queryFn: () => brandsApi.get(brandId!),
    enabled: isEdit,
  });
  const [config, setConfig] = useState<BrandStudioConfig | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Tracks how many ImagePicker children are currently uploading. Must live
  // ABOVE the `if (!config) return <Skeleton>` early-return at line 302 —
  // declaring it later would change hook count between renders and React
  // would throw "Rendered more hooks than during the previous render" as
  // soon as the query resolved.
  const [uploadingCount, setUploadingCount] = useState(0);

  // Seed once when source (brand or company) loads.
  useEffect(() => {
    if (config) return;
    // En edición se espera al kit (aunque no exista) para no sembrar dos veces.
    if (isEdit && brandQ.data && kitQ.isFetched) setConfig(configFromBrand(brandQ.data, kitQ.data ?? null));
    else if (!isEdit && company) setConfig(configFromCompany(company));
  }, [company, brandQ.data, kitQ.isFetched, kitQ.data, config, isEdit]);

  /**
   * PRD §8 Tab 1 — persist the full 5-color palette + experience defaults
   * to CoBrandKit. The kit is the single source of truth that the Xotski
   * bs_event snapshot reads on associate-survey and reset-identity. We
   * always upsert to the xotski-scoped variant.
   */
  const persistBrandKit = async (brand: { id: number }) => {
    if (!config) return;
    try {
      await brandkitApi.upsert(brand.id, {
        appType: 'xotski',
        campaignName: config.campaignName || undefined,
        primaryColor: config.primaryColor,
        secondaryColor: config.secondaryColor,
        accentColor: config.accentColor,
        backgroundColor: config.backgroundColor,
        fontColor: config.textColor,
        font: config.font,
        brandLogo: config.brandLogo ?? undefined,
        backgroundImage: config.backgroundImage ?? undefined,
        // Experience defaults (Tab 2) — flow into bs_event.create snapshot.
        language: config.language as any,
        collectName: config.collectName,
        collectEmail: config.collectEmail,
        collectPhone: config.collectPhone,
        ageGate: config.ageGate as any,
        demoAge: config.demoAge,
        demoGender: config.demoGender,
        demoEthnicity: config.demoEthnicity,
        demoNationality: config.demoNationality,
        celebrationTheme: config.celebrationTheme as any,
      } as any);
    } catch (e) {
      toast({
        tone: 'error',
        title: t('brandStudio.kitSaveFailed'),
        description: (e as Error).message,
      });
    }
  };

  const create = useMutation({
    mutationFn: (body: Partial<CoCompany>) => brandsApi.create(body),
    onSuccess: async (brand) => {
      await persistBrandKit(brand);
      queryClient.invalidateQueries({ queryKey: queryKeys.brands.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.companies.managers(company?.id ?? 0),
      });
      toast({ tone: 'success', title: t('brandStudio.brandCreated', { name: brand.name }) });
      navigate(`/brand/${brand.id}`);
    },
    onError: (e: Error) =>
      toast({ tone: 'error', title: t('brandStudio.createFailed'), description: e.message }),
  });

  const updateBrand = useMutation({
    mutationFn: (body: Partial<CoCompany>) => brandsApi.update(brandId!, body),
    onSuccess: async (brand) => {
      await persistBrandKit(brand);
      queryClient.invalidateQueries({ queryKey: queryKeys.brandKit.byCompany(brand.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.brands.detail(brand.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.brands.all });
      toast({ tone: 'success', title: t('brandStudio.brandUpdated', { name: brand.name }) });
      navigate(`/brand/${brand.id}`);
    },
    onError: (e: Error) =>
      toast({ tone: 'error', title: t('brandStudio.updateFailed'), description: e.message }),
  });

  const removeBrand = useMutation({
    mutationFn: () => brandsApi.remove(brandId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.brands.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.eventGroups.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.events.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.stations.all });
      toast({ tone: 'success', title: t('brandStudio.brandDeleted', { name: brandQ.data?.name ?? '' }) });
      navigate('/');
    },
    onError: (e: Error) =>
      toast({ tone: 'error', title: t('brandStudio.deleteFailed'), description: e.message }),
  });

  const loading = isEdit ? brandQ.isLoading : companyLoading;
  if (loading || !config) {
    return (
      <div className="page">
        <Skeleton className="mb-4 h-9 w-72 rounded" />
        <Skeleton className="h-96 w-full rounded-xl" />
      </div>
    );
  }

  const update = (delta: Partial<BrandStudioConfig>) =>
    setConfig((c) => (c ? { ...c, ...delta } : c));

  const saving = create.isPending || updateBrand.isPending;
  const pending = saving || removeBrand.isPending;

  /**
   * Brand form errors. Computed every render so the save button can disable
   * cleanly. Hex-color validation tolerates both #RGB and #RRGGBB. Logo and
   * background image are optional (they fall back to inherited values from
   * the parent company).
   */
  const HEX_COLOR = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;
  const brandErrors: { name?: string; primaryColor?: string; secondaryColor?: string; textColor?: string } = {};
  if (config.name.trim().length < 2) brandErrors.name = t('brandStudio.error.nameRequired');
  if (config.primaryColor && !HEX_COLOR.test(config.primaryColor)) {
    brandErrors.primaryColor = t('brandStudio.error.primaryHex');
  }
  if (config.secondaryColor && !HEX_COLOR.test(config.secondaryColor)) {
    brandErrors.secondaryColor = t('brandStudio.error.secondaryHex');
  }
  if (config.textColor && !HEX_COLOR.test(config.textColor)) {
    brandErrors.textColor = t('brandStudio.error.textHex');
  }
  // ImagePicker children call this while uploading to /uploads. We disable
  // Save during the upload so the payload always carries the fresh URL, not
  // a stale value (and never a raw data URI — see ImagePicker below).
  const onUploadingChange = (uploading: boolean) =>
    setUploadingCount((n) => Math.max(0, n + (uploading ? 1 : -1)));
  const isUploading = uploadingCount > 0;
  const canSave =
    Object.keys(brandErrors).length === 0 && !pending && !isUploading;

  const onSave = () => {
    if (!canSave) return;
    const payload: Partial<CoCompany> = {
      name: config.name.trim(),
      logo: config.brandLogo,
      backgroundColor: config.primaryColor,
      secondaryColor: config.secondaryColor,
      fontColor: config.textColor,
      backgroundImage: config.backgroundImage,
      industry: config.industry || null,
    };
    if (isEdit) updateBrand.mutate(payload);
    else create.mutate(payload);
  };

  return (
    <div className="page">
      {/* ── Page header ─────────────────────────────────────────────── */}
      <header className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="grid h-8 w-8 place-items-center rounded-md border border-[var(--color-xk-border)] text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]"
            title={t('brandStudio.back')}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1
              className="flex items-center gap-2 text-2xl font-black tracking-tight text-[var(--color-xk-lavender)]"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              <Sparkles className="h-5 w-5 text-[var(--color-accent-ink)]" />
              {t('brandStudio.title')}
            </h1>
            <p className="text-[13px] text-[var(--color-xk-text-muted)]">
              {isEdit ? (
                <>
                  {t('brandStudio.editing')}{' '}
                  <span className="text-[var(--color-xk-lavender)]">
                    {brandQ.data?.name ?? '…'}
                  </span>
                </>
              ) : (
                <>
                  {t('brandStudio.newBrandInherit')}{' '}
                  <span className="text-[var(--color-xk-lavender)]">{company?.name}</span>
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEdit && (
            <button
              type="button"
              onClick={() =>
                confirmDelete ? removeBrand.mutate() : setConfirmDelete(true)
              }
              disabled={pending}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-error)] px-3 py-2 text-xs font-extrabold uppercase tracking-widest text-[var(--color-error)] transition-colors hover:bg-[rgb(239_68_68/0.1)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
              {removeBrand.isPending
                ? t('brandStudio.deleting')
                : confirmDelete
                  ? t('brandStudio.confirmDelete')
                  : t('brandStudio.deleteBrand')}
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="rounded-lg border border-[var(--color-xk-border)] px-4 py-2 text-xs font-bold text-[var(--color-xk-lavender)] hover:bg-[var(--color-xk-surface)]"
          >
            {t('brandStudio.cancel')}
          </button>
          <button
            type="button"
            disabled={!canSave}
            onClick={onSave}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-xk-accent)] px-5 py-2 text-xs font-extrabold text-[var(--color-fg-on-accent)] transition-colors hover:bg-[var(--color-xk-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? t('brandStudio.saving') : isEdit ? t('brandStudio.saveChanges') : t('brandStudio.savePublish')}
          </button>
        </div>
      </header>

      {isEdit && confirmDelete && (
        <div className="mb-4 rounded-lg border border-[var(--color-error)] bg-[rgb(239_68_68/0.08)] p-3 text-xs text-[var(--color-fg-primary)]">
          <strong className="text-[var(--color-error)]">{t('brandStudio.confirmDeleteTitle')}</strong> {t('brandStudio.confirmDeleteBody')}{' '}
          <em>{t('brandStudio.confirmDeleteEmph')}</em> {t('brandStudio.confirmDeleteEnd')}
        </div>
      )}

      {Object.keys(brandErrors).length > 0 && (
        <div className="mb-4 rounded-lg border border-[var(--color-error)]/40 bg-[var(--color-error)]/10 p-3 text-xs">
          <p className="mb-1 font-bold text-[var(--color-error)]">
            {t(Object.keys(brandErrors).length === 1 ? 'brandStudio.fixOne' : 'brandStudio.fixMany', { count: Object.keys(brandErrors).length })}
          </p>
          <ul className="list-disc pl-5 text-[var(--color-fg-primary)]">
            {Object.entries(brandErrors).map(([k, msg]) => (
              <li key={k}>{msg}</li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Split panel ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(360px,420px)]">
        {/* LEFT — tabs + form */}
        <div className="rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] p-6">
          <IdentityTab config={config} update={update} onUploadingChange={onUploadingChange} />
        </div>

        {/* RIGHT — live preview */}
        <aside>
          <div className="sticky top-20">
            <div className="mb-3 flex items-center justify-between">
              <h2
                className="text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                {t('brandStudio.livePreview')}
              </h2>
              <span
                className="rounded bg-[var(--color-accent-15)] px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-widest text-[var(--color-accent-ink)]"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                {t('brandStudio.tab.identity')}
              </span>
            </div>
            <BrandPreview config={config} />
            <p className="mt-3 text-[11px] text-[var(--color-xk-text-muted)]">
              {t('brandStudio.previewFooter')}
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Tab components
// ════════════════════════════════════════════════════════════════════════════

interface TabBaseProps {
  config: BrandStudioConfig;
  update: (delta: Partial<BrandStudioConfig>) => void;
}

function IdentityTab({
  config,
  update,
  onUploadingChange,
}: TabBaseProps & { onUploadingChange?: (uploading: boolean) => void }) {
  const t = useT();
  return (
    <div className="space-y-5">
      <Hint message={t('brandStudio.identity.hint')} />

      <Field
        label={t('brandStudio.identity.brandName')}
        hint={t('brandStudio.identity.brandNameHint')}
      >
        <input
          type="text"
          value={config.name}
          onChange={(e) => update({ name: e.target.value })}
          placeholder={t('brandStudio.identity.brandNamePlaceholder')}
          autoFocus
          className={inputCls}
        />
      </Field>

      <Field
        label={t('brandStudio.identity.industry')}
        hint={t('brandStudio.identity.industryHint')}
      >
        <input
          type="text"
          value={config.industry}
          onChange={(e) => update({ industry: e.target.value })}
          placeholder={t('brandStudio.identity.industryPlaceholder')}
          className={inputCls}
        />
      </Field>

      <Field
        label={t('brandStudio.identity.campaignName')}
        hint={t('brandStudio.identity.campaignNameHint')}
      >
        <input
          type="text"
          value={config.campaignName}
          onChange={(e) => update({ campaignName: e.target.value })}
          placeholder={t('brandStudio.identity.campaignNamePlaceholder')}
          className={inputCls}
        />
      </Field>

      <Field label={t('brandStudio.identity.brandLogo')} hint={t('brandStudio.identity.brandLogoHint')}>
        <ImagePicker
          value={config.brandLogo}
          onChange={(v) => update({ brandLogo: v })}
          slotLabel={t('brandStudio.identity.noLogo')}
          preset="logo"
          onUploadingChange={onUploadingChange}
        />
      </Field>

      {/* QR thumbnail is configured per-event (bs_event.qrThumbnail), not at
          the brand level. Hidden here so users don't think this picker
          drives the actual QR rendered in surveys — that decision lives
          on the event page. */}

      <Field
        label={t('brandStudio.identity.backgroundImage')}
        hint={t('brandStudio.identity.backgroundImageHint')}
      >
        <ImagePicker
          value={config.backgroundImage}
          onChange={(v) => update({ backgroundImage: v })}
          slotLabel={t('brandStudio.identity.noImage')}
          preset="photo"
          onUploadingChange={onUploadingChange}
        />
      </Field>

      {/* 5-color palette */}
      <div>
        <SectionLabel>{t('brandStudio.identity.palette')}</SectionLabel>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <ColorField
            label={t('brandStudio.identity.color.primary')}
            description={t('brandStudio.identity.color.primaryDesc')}
            value={config.primaryColor}
            onChange={(v) => update({ primaryColor: v })}
          />
          <ColorField
            label={t('brandStudio.identity.color.secondary')}
            description={t('brandStudio.identity.color.secondaryDesc')}
            value={config.secondaryColor}
            onChange={(v) => update({ secondaryColor: v })}
          />
          <ColorField
            label={t('brandStudio.identity.color.accent')}
            description={t('brandStudio.identity.color.accentDesc')}
            value={config.accentColor}
            onChange={(v) => update({ accentColor: v })}
          />
          <ColorField
            label={t('brandStudio.identity.color.background')}
            description={t('brandStudio.identity.color.backgroundDesc')}
            value={config.backgroundColor}
            onChange={(v) => update({ backgroundColor: v })}
          />
          <ColorField
            label={t('brandStudio.identity.color.text')}
            description={t('brandStudio.identity.color.textDesc')}
            value={config.textColor}
            onChange={(v) => update({ textColor: v })}
          />
        </div>
      </div>

      <Field label={t('brandStudio.identity.brandFont')}>
        <select
          value={config.font}
          onChange={(e) => update({ font: e.target.value })}
          className={inputCls}
          style={{ fontFamily: `"${config.font}", sans-serif` }}
        >
          {FONTS.map((f) => (
            <option key={f} value={f} style={{ fontFamily: `"${f}", sans-serif` }}>
              {f}
            </option>
          ))}
        </select>
      </Field>
    </div>
  );
}

const inputCls =
  'w-full rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2.5 text-sm text-[var(--color-xk-lavender)] placeholder:text-[var(--color-xk-text-muted)] focus:border-[var(--color-accent-ink)] focus:outline-none';

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="mb-1.5 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
        {label}
      </div>
      {children}
      {hint && <div className="mt-1 text-[11px] text-[var(--color-xk-text-muted)]">{hint}</div>}
    </label>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
      {children}
    </div>
  );
}

function ColorField({
  label,
  description,
  value,
  onChange,
}: {
  label: string;
  description?: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] p-3">
      <div className="mb-1.5 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
        {label}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-8 w-10 cursor-pointer rounded border border-[var(--color-xk-border)] bg-transparent"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent text-[12px] uppercase tabular-nums text-[var(--color-xk-lavender)] focus:outline-none"
          style={{ fontFamily: 'var(--font-display)' }}
        />
      </div>
      {description && (
        <p className="mt-1 text-[10px] text-[var(--color-xk-text-muted)]">{description}</p>
      )}
    </div>
  );
}

function ImagePicker({
  value,
  onChange,
  slotLabel,
  preset = 'photo',
  onUploadingChange,
}: {
  value: string | null;
  onChange: (v: string | null) => void;
  slotLabel: string;
  preset?: CompressPreset;
  onUploadingChange?: (uploading: boolean) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  // Upload to /uploads instead of stashing the data URI in the value. The
  // `co_companies.logo` / `backgroundImage` columns are varchar(255) and a
  // base64 image overflows them immediately — see the "Data too long for
  // column 'logo'" incident. Now the column only ever stores the returned URL.
  const onFile = async (file: File) => {
    setUploading(true);
    onUploadingChange?.(true);
    try {
      const res = await uploadImage(file, { preset });
      onChange(res.url);
    } catch (err: any) {
      toast({
        tone: 'error',
        title: 'Upload failed',
        description: err?.message ?? 'Could not upload the image.',
      });
    } finally {
      setUploading(false);
      onUploadingChange?.(false);
      if (ref.current) ref.current.value = '';
    }
  };

  return (
    <div className="flex items-center gap-3">
      <div className="grid h-16 w-16 flex-none place-items-center overflow-hidden rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)]">
        {value ? (
          <img src={resolveImageUrl(value)} alt="" className="h-full w-full object-contain p-2" />
        ) : (
          <span className="text-[10px] text-[var(--color-xk-text-muted)]">{slotLabel}</span>
        )}
      </div>
      <input
        ref={ref}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        onChange={(e) => e.target.files?.[0] && void onFile(e.target.files[0])}
        className="hidden"
        disabled={uploading}
      />
      <button
        type="button"
        onClick={() => ref.current?.click()}
        disabled={uploading}
        className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-xk-border)] px-3 py-2 text-xs font-bold text-[var(--color-xk-lavender)] hover:bg-[var(--color-xk-bg-deep)] disabled:cursor-not-allowed disabled:opacity-60"
      >
        {uploading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Upload className="h-3.5 w-3.5" />
        )}
        {uploading ? 'Uploading…' : 'Upload'}
      </button>
      {value && !uploading && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="grid h-7 w-7 place-items-center rounded-md text-[var(--color-xk-text-muted)] hover:bg-[var(--color-xk-bg-deep)] hover:text-[var(--color-xk-lavender)]"
          title="Clear"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function Hint({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-[var(--color-accent-20)] bg-[var(--color-accent-05)] px-3 py-2.5">
      <Sparkles className="mt-0.5 h-3.5 w-3.5 flex-none text-[var(--color-accent-ink)]" />
      <p className="text-[12px] leading-relaxed text-[var(--color-xk-lavender)]">{message}</p>
    </div>
  );
}

// ── Live preview ───────────────────────────────────────────────────────────

function BrandPreview({ config }: { config: BrandStudioConfig }) {
  const name = config.name.trim() || 'Your brand';
  const gradient = `linear-gradient(135deg, ${config.primaryColor} 0%, var(--color-xk-bg) 100%)`;
  const buttonRadius =
    config.buttonStyle === 'pill' ? 999 : config.buttonStyle === 'square' ? 4 : 8;
  return (
    <div className="overflow-hidden rounded-2xl border border-[var(--color-xk-border)]">
      <div className="relative h-32 px-5 pt-5" style={{ background: gradient }}>
        <div className="flex items-center gap-3">
          <div className="grid h-12 w-12 place-items-center rounded-lg bg-[rgb(0_0_0/0.3)]">
            {config.brandLogo ? (
              <img
                src={resolveImageUrl(config.brandLogo)}
                alt=""
                className="max-h-10 max-w-[40px] object-contain"
              />
            ) : (
              <span
                className="text-base font-black"
                style={{
                  color: config.textColor,
                  fontFamily: `"${config.font}", sans-serif`,
                }}
              >
                {name.charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <div>
            <div
              className="text-[10px] font-extrabold uppercase tracking-widest"
              style={{
                color: config.textColor,
                opacity: 0.7,
                fontFamily: 'var(--font-display)',
              }}
            >
              Brand workspace
            </div>
            <div
              className="text-lg font-black tracking-tight"
              style={{ color: config.textColor, fontFamily: `"${config.font}", sans-serif` }}
            >
              {name}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[var(--color-xk-surface)] p-5">
        <div
          className="mb-1 text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {config.gameCTAEn || 'Spin to win'}
        </div>
        <div
          className="mb-4 text-xl font-black"
          style={{ color: config.textColor, fontFamily: `"${config.font}", sans-serif` }}
        >
          {config.campaignName.trim() || `${name} sampling`}
        </div>
        <button
          type="button"
          className="w-full px-4 py-2.5 text-sm font-extrabold transition-opacity hover:opacity-90"
          style={{
            backgroundColor: config.accentColor,
            color: '#0A0A0A',
            borderRadius: buttonRadius,
            fontFamily: `"${config.font}", sans-serif`,
          }}
        >
          {config.gameCTAEn || 'Continue'}
        </button>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Swatch color={config.primaryColor} label="Primary" />
          <Swatch color={config.secondaryColor} label="Secondary" />
          <Swatch color={config.accentColor} label="Accent" />
          <Swatch color={config.textColor} label="Text" />
        </div>
      </div>
    </div>
  );
}

function Swatch({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-1">
      <span
        className="block h-3.5 w-3.5 rounded border border-[rgb(255_255_255/0.15)]"
        style={{ backgroundColor: color }}
      />
      <span className="text-[10px] text-[var(--color-xk-text-muted)]">{label}</span>
    </div>
  );
}
